<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Hero Section -->
    <section class="relative overflow-hidden" style="background: linear-gradient(to bottom right, #A3E4DB, #C4E49F);">

      <div class="absolute inset-0 bg-white/30 backdrop-blur-sm"></div>

      <!-- Hero Content -->
      <div class="relative container mx-auto px-4 sm:px-6 py-8 sm:py-12 lg:py-16">
        <div class="flex flex-col lg:grid lg:grid-cols-2 gap-6 lg:gap-8 items-center">
          <!-- Left Content - 在小螢幕上使用 flex，大螢幕用 grid -->
          <div class="text-gray-800 relative w-full lg:order-1">
            <h1 class="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4 leading-tight text-center lg:text-left">
              智光系統 <span style="color: #F4D35E;">解決方案</span>
            </h1>
            <p class="text-base sm:text-lg lg:text-xl mb-6 text-gray-700 leading-relaxed text-center lg:text-left">
              提供專業、直覺、功能齊全的企業管理系統，助您輕鬆管理業務流程
            </p>

            <!-- Feature Pills -->
            <div class="flex flex-wrap gap-2 sm:gap-3 mb-6 justify-center lg:justify-start">
              <span
                class="px-3 sm:px-4 py-2 bg-white/70 rounded-full text-xs sm:text-sm font-medium text-gray-700 shadow-sm">
                直覺操作介面
              </span>
              <span
                class="px-3 sm:px-4 py-2 bg-white/70 rounded-full text-xs sm:text-sm font-medium text-gray-700 shadow-sm">
                專業形象網站
              </span>
              <span
                class="px-3 sm:px-4 py-2 bg-white/70 rounded-full text-xs sm:text-sm font-medium text-gray-700 shadow-sm">
                ALL IN ONE
              </span>
              <span
                class="px-3 sm:px-4 py-2 bg-white/70 rounded-full text-xs sm:text-sm font-medium text-gray-700 shadow-sm">
                完整財務管理
              </span>
            </div>

            <div class="text-center lg:text-left">
              <router-link to="/priceplan">
                <button
                  class="text-black px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  style="background-color: #F4D35E; --tw-bg-opacity: 1;"
                  onmouseover="this.style.backgroundColor='#fce788'"
                  onmouseout="this.style.backgroundColor='#F4D35E'"
                >
                  立即體驗
                </button>
              </router-link>
            </div>

          </div>

          <!-- Right Content - Image Slider 修正輪播圖 -->
          <div class="relative w-full lg:order-2">
            <div class="bg-white bg-opacity-10 rounded-2xl p-2 sm:p-4 backdrop-blur-sm">
              <div class="w-full h-48 sm:h-56 md:h-64 lg:h-80 rounded-xl overflow-hidden shadow-2xl bg-gray-100">
                <swiper class="w-full h-full swiper-container-fixed" :loop="true" :modules="modules"
                  :pagination="{ clickable: true }" :slides-per-view="1" :space-between="0"
                  :autoplay="{ delay: 4000, disableOnInteraction: false }" @swiper="onSwiper"
                  @slideChange="onSlideChange">
                  <swiper-slide v-for="(item, index) in slideImages" :key="index" class="slide-container">
                    <div class="w-full h-full">
                      <img :src="item.src" class="slide-image" :alt="item.alt" @error="handleImageError"
                        @load="handleImageLoad">
                    </div>
                  </swiper-slide>
                </swiper>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Decorative Elements -->
      <div class="absolute top-0 right-0 w-1/3 h-full opacity-10">
        <div class="w-full h-full bg-gradient-to-l from-white to-transparent"></div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-6">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-gray-800 mb-4">核心功能特色</h2>
          <p class="text-xl text-gray-600 max-w-2xl mx-auto">
            智光系統提供全方位的企業管理解決方案，讓您的業務運營更加高效
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">

          <!-- Card 1 - 淺珊瑚 / 暖橙紅 -->
          <div
            class="group bg-[#E9967A] p-8 rounded-2xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
            <div
              class="w-16 h-16 bg-white/30 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4">
                </path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-white mb-3">直覺的操作介面</h3>
            <p class="text-white/90">簡潔易用的操作介面，讓您快速上手，提升工作效率</p>
          </div>

          <!-- Card 2 - 柔黃 -->
          <div
            class="group bg-[#F4D35E] p-8 rounded-2xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
            <div
              class="w-16 h-16 bg-white/30 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9"></path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-white mb-3">專業形象網站</h3>
            <p class="text-white/90">打造專業企業形象，提升品牌價值與客戶信任度</p>
          </div>

          <!-- Card 3 - 淺草綠 -->
          <div
            class="group bg-[#A6C48A] p-8 rounded-2xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
            <div
              class="w-16 h-16 bg-white/30 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10">
                </path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-white mb-3">功能齊全 ALL IN ONE</h3>
            <p class="text-white/90">整合所有必要功能，一套系統滿足企業全方位需求</p>
          </div>

          <!-- Card 4 - 青藍色 -->
          <div
            class="group bg-[#5FB4BF] p-8 rounded-2xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
            <div
              class="w-16 h-16 bg-white/30 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z">
                </path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-white mb-3">完整財務系統管理</h3>
            <p class="text-white/90">全面的財務管理功能，讓企業財務狀況一目了然</p>
          </div>
        </div>
      </div>
    </section>

    <!-- News Section -->
    <section class="py-20 bg-gray-50">
      <div class="container mx-auto px-6">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-gray-800 mb-4">最新消息</h2>
          <p class="text-xl text-gray-600">掌握智光系統最新動態與產品資訊</p>
        </div>

        <div class="grid lg:grid-cols-3 gap-8 mb-12">
          <!-- Featured News -->
          <div class="lg:col-span-2">
            <div class="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div class="relative">
                <img src="@/assets/img/7ab9ccc609e1d8d035c15df2c8301d5d-1337222.jpg" class="w-full h-64 object-cover"
                  alt="Featured news">
                <div class="absolute top-4 left-4">
                  <span class="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    重要公告
                  </span>
                </div>
              </div>
              <div class="p-8">
                <h3 class="text-2xl font-bold text-gray-800 mb-4">
                  智光已進駐台中軟體園區，歡迎舊雨新知，蒞臨指導
                </h3>
                <p class="text-gray-600 mb-6">
                  我們很高興宣布智光系統正式進駐台中軟體園區，為客戶提供更優質的服務體驗...
                </p>
                <button class="text-blue-500 hover:text-blue-600 font-medium flex items-center group">
                  閱讀更多
                  <svg class="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" fill="none"
                    stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- News List -->
          <div class="space-y-6">
            <div class="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div class="flex gap-4">
                <img src="@/assets/img/F1463042289467.jpg" class="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                  alt="News thumbnail">
                <div class="flex-1">
                  <h4 class="font-bold text-gray-800 mb-2 line-clamp-2">
                    訊息e點通APP功能特色產品特色
                  </h4>
                  <p class="text-sm text-gray-500">2024-01-15</p>
                </div>
              </div>
            </div>

            <div class="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div class="flex gap-4">
                <img src="@/assets/img/math-education-huang-min-hsiung-20170728-02.jpg"
                  class="w-20 h-20 object-cover rounded-lg flex-shrink-0" alt="News thumbnail">
                <div class="flex-1">
                  <h4 class="font-bold text-gray-800 mb-2 line-clamp-2">
                    智光i學園最新課程上線
                  </h4>
                  <p class="text-sm text-gray-500">2024-01-10</p>
                </div>
              </div>
            </div>

            <div class="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div class="flex gap-4">
                <img src="@/assets/img/article-62598c9e71079.jpeg"
                  class="w-20 h-20 object-cover rounded-lg flex-shrink-0" alt="News thumbnail">
                <div class="flex-1">
                  <h4 class="font-bold text-gray-800 mb-2 line-clamp-2">
                    速算家雲端版協同操作體驗
                  </h4>
                  <p class="text-sm text-gray-500">2024-01-05</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center">
          <router-link to="/newnews">
            <button
                  class="text-black px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  style="background-color: #F4D35E; --tw-bg-opacity: 1;"
                  onmouseover="this.style.backgroundColor='#fce788'"
                  onmouseout="this.style.backgroundColor='#F4D35E'"
                >
              查看更多消息
            </button>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-6">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-gray-800 mb-4">服務項目</h2>
          <p class="text-xl text-gray-600">提供全方位的企業數位化解決方案</p>
        </div>

        <div class="grid md:grid-cols-3 gap-8">
          <!-- Service 1 淺珊瑚 / 暖橙紅 -->
          <div
            class="bg-[#E9967A] rounded-2xl p-8 text-white relative overflow-hidden">
            <div class="relative z-10">
              <h3 class="text-2xl font-bold mb-4">智光系統項目</h3>
              <ul class="space-y-2 text-white/90">
                <li>• 企業資源規劃系統</li>
                <li>• 客戶關係管理系統</li>
                <li>• 人力資源管理系統</li>
              </ul>
              <button
                class="mt-6 bg-white text-[#E9967A] px-6 py-2 rounded-lg font-medium hover:bg-white/90 transition-colors duration-300">
                了解更多
              </button>
            </div>
            <div class="absolute -top-4 -right-4 w-24 h-24 bg-white opacity-20 rounded-full"></div>
          </div>

          <!-- Service 2 淺草綠 / 柔和綠色 -->
          <div
            class="bg-[#A6C48A] rounded-2xl p-8 text-white relative overflow-hidden">
            <div class="relative z-10">
              <h3 class="text-2xl font-bold mb-4">進存服務項目</h3>
              <ul class="space-y-2 text-white/90">
                <li>• 庫存管理系統</li>
                <li>• 進銷存管理</li>
                <li>• 供應鏈管理</li>
              </ul>
              <button
                class="mt-6 bg-white text-[#A6C48A] px-6 py-2 rounded-lg font-medium hover:bg-white/90 transition-colors duration-300">
                了解更多
              </button>
            </div>
            <div class="absolute -top-4 -right-4 w-24 h-24 bg-white opacity-20 rounded-full"></div>
          </div>

          <!-- Service 3 青藍色（海水藍） -->
          <div
            class="bg-[#5FB4BF] rounded-2xl p-8 text-white relative overflow-hidden">
            <div class="relative z-10">
              <h3 class="text-2xl font-bold mb-4">智光i學園</h3>
              <ul class="space-y-2 text-white/90">
                <li>• 線上教育訓練</li>
                <li>• 系統操作指南</li>
                <li>• 專業認證課程</li>
              </ul>
              <button
                class="mt-6 bg-white text-[#5FB4BF] px-6 py-2 rounded-lg font-medium hover:bg-white/90 transition-colors duration-300">
                了解更多
              </button>
            </div>
            <div class="absolute -top-4 -right-4 w-24 h-24 bg-white opacity-20 rounded-full"></div>
          </div>
        </div>
      </div>
    </section>


    <!-- Case Studies / Clients Logo Section -->
    <section class="py-20 bg-gray-50">
      <div class="container mx-auto px-6">
        <div class="text-center mb-12">
          <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">客戶案例</h2>
          <p class="text-lg text-gray-600">眾多補教業者與教育單位選擇智光系統，一同邁向數位轉型</p>
        </div>

        <!-- Region Tabs -->
        <div class="flex justify-center mb-8">
          <div class="flex bg-white rounded-lg shadow-sm p-1">
            <button v-for="(region, index) in regions" :key="index" @click="currentRegion = index" :class="[
              'px-6 py-2 rounded-md font-medium transition-all duration-200',
              currentRegion === index
                ? 'bg-[#E9967A] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            ]">
              {{ region.name }}
            </button>
          </div>
        </div>

        <!-- Carousel Container -->
        <div class="relative overflow-hidden">
          <div class="flex transition-transform duration-500 ease-in-out"
            :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
            <div v-for="(slide, slideIndex) in currentRegionSlides" :key="slideIndex" class="w-full flex-shrink-0">
              <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                <div v-for="(client, clientIndex) in slide" :key="clientIndex"
                  class="flex items-center justify-center p-4 bg-white hover:bg-gray-50 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md min-h-[80px]">
                  <div class="text-center">
                    <div class="h-12 flex items-center justify-center mb-2">
                      <span class="text-sm font-medium text-gray-700 line-clamp-2">{{ client }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Arrows -->
        <div class="flex justify-center items-center mt-8 space-x-4">
          <button @click="prevSlide" :disabled="currentSlide === 0"
            class="p-2 rounded-full bg-white shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>

          <!-- Slide Indicators -->
          <div class="flex space-x-2">
            <button v-for="(slide, index) in currentRegionSlides" :key="index" @click="currentSlide = index" :class="[
              'w-3 h-3 rounded-full transition-all duration-200',
              currentSlide === index
                ? 'bg-[#E9967A]'
                : 'bg-gray-300 hover:bg-gray-400'
            ]"></button>
          </div>

          <button @click="nextSlide" :disabled="currentSlide === currentRegionSlides.length - 1"
            class="p-2 rounded-full bg-white shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>

        <!-- Client Count Display -->
        <div class="text-center mt-6">
          <p class="text-gray-600">
            {{ regions[currentRegion].name }} - 共 {{ regions[currentRegion].clients.length }} 個合作夥伴
          </p>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 -mb-3 bg-white">
      <div class="container mx-auto px-6 text-center relative z-10">
        <h2 class="text-4xl font-bold text-gray-800 mb-6">
          準備開始您的數位轉型之旅了嗎？
        </h2>
        <p class="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
          立即聯繫我們，讓智光系統為您的企業打造專屬的管理解決方案
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link to="/contactus">
            <button
              class="border-2 border-gray-800 text-gray-800 bg-white/70 hover:bg-gray-800 hover:text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl">
              立即聯繫我們
            </button>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
/*eslint-disable*/
import { useStore } from "vuex";
import { ref, computed, watch } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay, Pagination, Scrollbar } from 'swiper/modules';
import { useRouter, useRoute } from "vue-router";
import { getLineLoginCallback, authorize } from '@/api/api'
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

const store = useStore()
const router = useRouter()
const route = useRoute()
const bannerData = ref([])
const newsData = ref([])

// 圖片資料
const slideImages = ref([
  {
    src: require('@/assets/img/00efb1e1d443521316966bc7e141fb41-38869.jpg'),
    alt: '智光系統展示'
  },
  {
    src: require('@/assets/img/7ab9ccc609e1d8d035c15df2c8301d5d-133722.jpg'),
    alt: '系統功能'
  },
  {
    src: require('@/assets/img/article-62598c9e71079.jpeg'),
    alt: '企業解決方案'
  }
])

// Swiper modules
const modules = [Autoplay, Pagination, Scrollbar];

const isMobile = computed(() => {
  return store.state.isMobile
})

const getParameterByName = (name, url) => {
  if (!url) url = window.location.href;
  name = name.replace(/[[\]]/g, '\\$&');
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

const checkLineLogin = () => {
  let payload = {
    code: '',
    state: ''
  }
  if (route.query.code && route.query.state) {
    payload = {
      code: route.query.code,
      state: route.query.state
    }
  } else if (window.location.href != '') {
    payload = {
      code: getParameterByName('code', window.location.href),
      state: getParameterByName('state', window.location.href)
    }
  }
  if (payload.code && payload.state) {
    getLineLoginCallback(payload).then((res) => {
      if (res.data.status) {
        if (res.data.data.state == 0) {
          store.commit('setToken', res.data.data.data)
          authorize().then((res1) => {
            if (res1.data.status) {
              store.commit('setUser', res1.data.data)
              router.push('/home');
            } else {
              console.log(res1.data.message)
            }
          }).catch((err) => { })
        } else if (res.data.data.state == 1) {
          store.commit('setLineId', res.data.data.data)
          router.push('/profile')
        } else if (res.data.data.state == 2) {
          store.commit('setToken', res.data.data.data)
          authorize().then((res1) => {
            if (res1.data.status) {
              store.commit('setStudentIdByLine', res.data.data.data)
              store.commit('setUser', res1.data.data)
              router.push('/profile')
            } else {
              console.log(res1.data.message)
            }
          }).catch((err) => { })
        }
      } else {
        console.log(res.data.message)
      }
    }).catch((err) => { })
  }
}

const init = async () => {
  authorize().then((res) => {
    if (res.data.status) {
      store.commit('setUser', res.data.data)
    } else {
      checkLineLogin()
    }
  }).catch((err) => {
    checkLineLogin()
  })

  bannerData.value = [
    { ImageUrl: require('@/assets/img/00efb1e1d443521316966bc7e141fb41-38869.jpg') },
    { ImageUrl: require('@/assets/img/carousel-1.jpg') }
  ]

  for (let item of bannerData.value) {
    const img = new Image();
    img.src = item.ImageUrl;
    img.onload = function () {
      item.width = img.width
      item.height = img.height
    }
  }
}

init()

// Swiper 事件處理
const onSwiper = (swiper) => {
  // console.log('Swiper initialized:', swiper)
}

const onSlideChange = () => {
  // console.log('Slide changed')
}


// === 初始化資料 ===
const currentRegion = ref(0)
const currentSlide = ref(0)
const itemsPerSlide = 12

const regions = ref([
  {
    name: '北部地區',
    clients: [
      '冠群文理補習班', '冠能文理補習班', '金恩美語補習班', '資優文理補習班',
      '立勝文理補習班', '大象語文補習班', '巨江文理補習班', '希望文理補習班',
      '達正文理補習班', '進階美語補習班', '萬方文理補習班', '佳陽文理補習班',
      '吉尼熊文理補習班', '喬斯美語補習班', '懷恩文理補習班', '興隆文理補習班',
      '華廣設計學苑', '喬治雅美語補習班', '蔡聰達文理補習班', '傑文美語仁愛分校',
      '傑文美語信義分校', '丁老師文理補習班', '喬登美語秀朗分校', '林鴻文理補習班',
      '泥格子文理補習班', '藍艾比文理補習班', '翰聲文理補習班', '志聖研究所補習班',
      '格蘭英語補習班', '華視訓練中心', '復興美工設計學苑', '王耀數學補習班',
      '美爾頓英語補習班', '劍喬語文中心', '莫大衛美語補習班', '凱特美語補習班',
      '匡藤文理補習班', '學策文理補習班', '文魁文理補習班', '小象托兒所',
      '小豆芽托兒所', '大老鷹托兒所', '神奇小子托兒所', '瀚思幼兒托兒所',
      '板橋康園托兒所', '多明哥補習班', '夏恩英語板橋校', '夏恩英語羅斯福校',
      '夏恩英語東湖校', '夏恩英語內湖校', '夏恩英語土城校', '夏恩英語木柵校',
      '夏恩英語文化校', '夏恩英語新莊校', '夏恩英語永和校', '夏恩英語三峽校',
      '夏恩英語正義校', '夏恩英語重新校', '群仁文教機構', '冠宇文理補習班',
      '學習工廠興化分校', '浩域企業管理顧問', '根基文理補習班', '喬登美語大直分校',
      '童心緣文理補習班', '小敦儒才藝補習班', '佳盈幼稚園', '長峰文理補習班',
      '東門日語補習班', '思達文理補習班', '新電國際', '航發協會',
      '喬登美語永福分校', '喬登美語中和分校', '喬登美語三重分校', '喬登美語重榮分校',
      '喬登美語民權分校', '喬登美語後埔分校', '喬登美語長春分校', '喬登美語礪德分校',
      '精英文理補習班', '敦文文理補習班', '日大文理補習班', '夏傑文理補習班',
      '文群文理補習班', '超人珠心算補習班', '陶倢文理補習班', '財鑫文教機構',
      '立誠文理補習班', '佳音英語世貿分校', '英美英語東區分校', '喬登美語格登分校',
      '喬登美語北大分校', '地球花園補習班', '商宏文理補習班', '超人文理補習班',
      '哈波美語永福校', '弋果美語東湖校', '弋果美語內湖校', '般若文理補習班',
      '葆體瑜珈古亭校', '葆體瑜珈松江校', '吉的堡美語中山分校', '佳音英語天母分校',
      '佳音英語民生分校', '佳音英語致理分校', '佳音英語東湖分校', '佳音英語頂溪分校',
      '佳音英語蘆洲分校', '佳音英語傑出兒童才藝', '芝蔴街美語承德分校', '芝蔴街美語五股分校',
      '芝蔴街美語基隆分校', '芝蔴街美語福和分校', '小哈波美語南港分校', '小哈波美語深美分校',
      '小哈波美語蘆洲分校', '小哈波美語五股分校', '博群文理補習班', '成巨集電腦補習班',
      '金牌文理補習班', '辰育文理補習班', '朗呢飛文理補習班', '佳升文理補習班',
      '英揚國際補習班', '佳音英語永平分校', '永老師烹飪補習班', '張老師留學中心',
      '長頸鹿美語信義分校', '佳音英語土城清水校', '貝師特文理補習班', '顥學文理補習班',
      '長頸鹿美語錦和分校', '長頸鹿美語(羅氏補習班)', '長頸鹿美語中和分校',
      '長頸鹿美語三重光榮分校', '長頸鹿美語大同分校', '哥大蒙特梭利幼稚園', '哥大英語補習班',
      '環宇語文商業短期補習班', '李福裕補習班(階梯美語)', '信華補習班(華興連鎖)',
      '傑麗兒智能提升語文學校', '大種子學園(數學概念館)', '仟業國際教育諮詢機構',
      '三一寰宇文教協會', '門得揚永平數位文理補習班', '門得揚麗山數位文理補習班',
      '門得揚文山數位文理補習班', '繁田塾日語補習班', '弋果美語林口民權校',
      '弋果美語復興分校', '臺灣法國協會補習班', '維也納文理補習班', '喬伊坊文理補習班',
      '舞動英語補習班', '長頸鹿美語東湖分校', '寰宇文理補習班敦南校', '寰宇文理補習班信義校',
      '世宗韓語補習班', '正鏵文理補習班', '吉的堡美語五華分校'
    ]
  },
  {
    name: '桃竹苗地區',
    clients: [
      '北聯文教機構', '北聯文教公館分校', '台師菁英補習班', '方華文理補習班', '天毓文理補習班',
      '立弘文理補習班', '勤學文理補習班', '北一文理補習班', '康寧幼稚園', '喬登美語龍星分校',
      '學益文理補習班', '巴比美語補習班', 'TOP語文補習班', '知新文理補習班', '西園文理補習班',
      '新學友文理補習班', '山水音樂藝術學苑', '阿波羅文理補習班', '智捷文教事業機構', '國際才藝專業教室',
      '孩子王美語補習班', '孩子國美語補習班', '金博士文理補習班', '仁宏珠心算補習班', '洪藝瑋文理補習班',
      '快樂瑪莉安補習班', '青山語文短期補習班', '北京人國際文教機構', '呂老師珠心算補習班', '小哈波美語內湖分校',
      '寰宇自然文理補習班', '彼得瑞特美語補習班', '群仁文教機構', '冠宇文理補習班', '學習工廠興化分校',
      '浩域企業管理顧問', '根基文理補習班', '喬登美語大直分校', '童心緣文理補習班', '小敦儒才藝補習班',
      '佳盈幼稚園', '長峰文理補習班', '東門日語補習班', '思達文理補習班', '新電國際', '航發協會',
      '喬登美語永福分校', '喬登美語中和分校', '喬登美語三重分校', '喬登美語重榮分校', '喬登美語民權分校',
      '喬登美語後埔分校', '喬登美語長春分校', '喬登美語礪德分校', '精英文理補習班', '敦文文理補習班',
      '日大文理補習班', '夏傑文理補習班', '文群文理補習班', '超人珠心算補習班', '陶倢文理補習班',
      '財鑫文教機構', '立誠文理補習班', '佳音英語世貿分校', '英美英語東區分校', '喬登美語格登分校',
      '喬登美語北大分校', '地球花園補習班', '商宏文理補習班', '超人文理補習班', '哈波美語永福校',
      '弋果美語東湖校', '弋果美語內湖校', '般若文理補習班', '葆體瑜珈古亭校', '葆體瑜珈松江校',
      '吉的堡美語中山分校', '佳音英語天母分校', '佳音英語民生分校', '佳音英語致理分校', '佳音英語東湖分校',
      '佳音英語頂溪分校', '佳音英語蘆洲分校', '佳音英語傑出兒童才藝', '芝蔴街美語承德分校', '芝蔴街美語五股分校',
      '芝蔴街美語基隆分校', '芝蔴街美語福和分校', '小哈波美語南港分校', '小哈波美語深美分校',
      '小哈波美語蘆洲分校', '小哈波美語五股分校', '博群文理補習班', '成巨集電腦補習班', '金牌文理補習班',
      '辰育文理補習班', '朗呢飛文理補習班', '佳升文理補習班', '英揚國際補習班', '佳音英語永平分校',
      '永老師烹飪補習班', '張老師留學中心', '長頸鹿美語信義分校', '佳音英語土城清水校', '貝師特文理補習班',
      '顥學文理補習班', '長頸鹿美語錦和分校', '長頸鹿美語(羅氏補習班)', '長頸鹿美語中和分校',
      '長頸鹿美語三重光榮分校', '長頸鹿美語大同分校', '哥大蒙特梭利幼稚園', '哥大英語補習班',
      '環宇語文商業短期補習班', '李福裕補習班(階梯美語)', '信華補習班(華興連鎖)', '傑麗兒智能提升語文學校',
      '大種子學園(數學概念館)', '仟業國際教育諮詢機構', '三一寰宇文教協會', '門得揚永平數位文理補習班',
      '門得揚麗山數位文理補習班', '門得揚文山數位文理補習班', '繁田塾日語補習班', '弋果美語林口民權校',
      '弋果美語復興分校', '臺灣法國協會補習班', '維也納文理補習班', '喬伊坊文理補習班', '舞動英語補習班',
      '長頸鹿美語東湖分校', '寰宇文理補習班敦南校', '寰宇文理補習班信義校', '世宗韓語補習班',
      '正鏵文理補習班', '吉的堡美語五華分校'
    ]
  },
  {
    name: '中部地區',
    clients: [
      '翔宇航空補習班', '文山社區大學', '彰化縣公民大學', '彰化婦女社區大學', '敦學教育中心',
      '佳華文理補習班', '大道文理補習班', '創意補習班(崇德)', '創意補習班(文心)', '陽明文教機構',
      '學習工廠成功分校', '瀚學士文理習班', '正新文理補習班', '最嘉才藝中心', '建勝才藝中心',
      '亞頓語文學苑', '弋果美語彰化分校', '弋果美語崇德分校', '弋果美語河南分校', '優蓓仕幼稚園',
      '優蓓仕美語補習班', '夏恩英語彰化分校', '夏恩英語崇德分校', '台中王牌補習班', '建才文理補習班',
      '冠群文理補習班', '士捷文理補習班', '智慧文理補習班', '建林文理補習班', '智優文理補習班',
      '優比文理補習班', '建文文理補習班', '旭陽文理補習班', '育群文理補習班', '林彩惠日語學苑',
      '翰學文理補習班', '康軒文理補習班', '菁湛文理補習班', '高峰文理補習班', '快樂寶貝幼稚園',
      '友達正文理補習班', '博士達文理補習班', '喬登美語文山分校', '百科文理補習班', '夏根文理補習班',
      '新能量補習班', '魔力英檢補習班', '第一文理補習班(梧棲)', '聯合文理補習班(梧棲)',
      '建國補習安親班(民間)', '芝蔴街美語沙鹿分校', '美加英語會話補習班', '群智文理補習班',
      '潭秀莘大文理補習班', '小邏輯文理補習班', '吉瑞福文理補習班', '佳倫文理補習班', '冠譽文理補習班',
      '向上心文理補習班', '維多利亞英檢中心', '龍鳳文理補習班', '劉蔚文理補習班', '曙光文理補習班',
      '歐森幼稚園', '向日葵幼稚園', '新育才文理補習班', '瑞德美語補習班', '揚智文理補習班',
      '新超群文理補習班', '威肯文理補習班', '薇格美語補習班', '佳音英語南屯分校', '佳音英語員林分校',
      '佳音英語大裡分校', '喬登美語梧棲分校', '喬登美語集集分校', '喬登美語豐陽分校',
      '喬登美語草屯分校', '喬登美語大甲分校', '資優堡美語安親班', '諾貝兒文理補習班',
      '官鼎鈞文理補習班', '多向思考文教機構', '啄木鳥文理補習班', '企鵝家族文教機構',
      '學誼文理補習班', '馬可美語補習班', '丹尼兒幼稚園', '卓越文理補習班', '立志文理補習班',
      '康活瑜珈補習班', '金榜文理補習班', '科奇文理補習班', '曉明長青大學', '台中長青學院',
      '池冠游泳池文心分校', '池冠游泳池東山分校', '松林綠香(游泳池)公司', '北聯文教機構台中分校',
      '波特電腦美語忠孝分校', '小傑克邏輯學苑中華總校', '小傑克邏輯學苑建平分校', '尤秀文教機構東芳分校',
      '何老師文理補習班', '劍橋文理補習班', '謝氏文理補習班', '碩達文理補習班', '新資優補習班',
      '阿拉丁安親班', 'E-Z語言補習班', '哈佛幼稚園(太平)', '國際村美語補習班', '高僑文理補習班',
      '由申甲文理補習班育德校', '由申甲文理補習班大墩校', '林子文理補習班民生校', '林子文理補習班和平校',
      '佳音英語二林分校', '中市民眾服務社電腦班', '柏瑟管理顧問公司(全體系)', '展鵬文教機構(四技二專)',
      '環宇語文補習班台中分校', '環宇語文補習班逢甲分校', '佩瑞絲美容補習班', '顧德文教機構大墩分校',
      '立仁補習班(美樂蒂分校)', '獻文補習班(美樂蒂分校)', '奇美補習班(美樂蒂分校)', '建達補習班(美樂蒂分校)',
      '琴律才藝(美樂蒂分校)', '美樂蒂美語(彰化分校)', '小哈波電腦美語南投分校', '小哈波電腦美語文心分校',
      '小哈波電腦美語東山分校', '戴爾美語補習班', '博學門文理補習班', '大東華補習班', '光寰外語補習班',
      '典範文理補習班', '普拉斯文理補習班', '翊志文理補習班', '夏恩英語大裡分校', '夏爾歐語補習班',
      '故事講堂補習班', '佳陽文理補習班', '鮑金秀文理補習班', '佳音英語惠文分校', '吉的堡美語梧棲分校',
      '育立文理補習班'
    ]
  },
  {
    name: '南部地區',
    clients: [
      '寰宇美日語(高雄)', '寰宇美日語(台南)', '學一餐旅航空學校', '萬冠航空(成功校)',
      '東展航空(民族校)', '冠南文理補習班', '喬英文理補習班', '旭晨外語補習班',
      '旭捷文理補習班', '日群文理補習班', '國際學友補習班', '翔優文理補習班',
      '元大文理補習班', '劍橋美語補習班', '大人物文理補習班', '小八哥美語補習班',
      '典範文理補習班', '文馨文理補習班', '彩虹苑文理補習班', '力邦文教機構',
      '育英文理補習班', '柏大文理補習班', '高如峰文理補習班', '達成文理補習班',
      '第一文理補習班', '禾立文理補習班', '哈佛通文理補習班', '強納生美語補習班',
      '全文文理補習班', '金字塔文理補習班', '天下文理補習班', '欽聖文理補習班',
      '威爾英語補習班', '非凡文理補習班', '新傳文理補習班', '大大文理補習班',
      '文德文理補習班', '師大文理補習班', '群儒文理補習班', '精通美語補習班',
      '藝私塾美術教室', '三愛文理補習班', '龍騰文理補習班', '大林外語補習班',
      '聖宜文理補習班', '優仕教育學院', '平成日語補習班', '柳毅文理補習班',
      '孫老師文理補習班', '太鼓教育機構', '柏喬文理補習班', '鳳新文理補習班',
      '青青美語補習班', '博愛文理補習班', '豈巨美羽補習班', '長頸鹿美語五王分校',
      '長頸鹿美語樸子分校', '長頸鹿美語古坑分校', '長頸鹿美語橋頭分校',
      '芝蔴街美語鳳山分校', '芝蔴街美語西螺分校', '芝蔴街美語大社分校',
      '美樂蒂美語二侖分校', '吉的堡美語歸仁分校', '永新英語補習班', '泓科文理補習班',
      '永晴安親班', '洪老師文理補習班', '騰峰文理補習班', '懷恩美語補習班',
      '佳能美語補習班', '雨果幼稚園', '巧智外語補習班', '文星文理補習班',
      '大衛美語補習班', '明祥文理補習班', '彼得潘文理補習班', '台大文理補習班',
      '儒苑文理補習班', '儒園文理補習班', '儒田文理補習班', '學達文理補習班',
      '聯美外語補習班', '高雄王牌文理補習班', '美利肯美語橋頭分校', '新世紀文理補習班',
      '資優生美語補習班', '喬登美語南區管理處', '百博文教事業機構', '蒄蒄藝術音樂學苑',
      '智明文理補習班(屏東)', '第一志願文理補習班', '佑昌文理珠心算補習班',
      '爬格子國語文補習班', '華品兒童教育學院', '塞尚專業美語', '快樂三秒補習班',
      '文欣外語補習班', '隸英美語補習班', '吉的堡美語重慶分校', '吉的堡美語明華分校',
      '哈佛文理補習班阿蓮分校', '哈佛文理補習班岡山分校', '哈佛文理補習班竹圍分校',
      '吉米貝爾文理補習班', '聖約翰文理補習班', '崇德學苑補習班', '新資優生補習班',
      '熊老師文理補習班', '綠樹美語補習班', '映葉設計補習班', '小哈波電腦美語北港分校',
      '小哈波電腦美語中埔分校', '小哈波電腦美語水上分校', '小哈波電腦美語新生分校',
      '李嵩資優升高中中心(龍華)', '李嵩資優升高中中心(鳳山)', '李嵩資優升高中中心(路竹)',
      '李嵩資優升高中中心(岡山)', '李嵩資優升高中中心(博碩)', '李嵩資優升高中中心(崇明)',
      '顧德資優文教機構(全體系)', '資優生教育中心新光分校', '杏冠安親班美語學校',
      '德生教會附設社區大學', '林肯國際美語教育機構', '長頸鹿美語賢北分校',
      '長頸鹿美語莊敬分校', '佳音英語中正分校', '佳音英語南科分校', '佳音英語新興分校',
      '佳音英語新歸仁分校', '弋果美語崇學分校', '學謙美語補習班', '康翎文理補習班',
      '西元文理補習班', '西元文理補習班仁武校', '電時代文理補習班', '同心文理補習班',
      '立暉數位學苑', '蒲公英美語補習班', '萬國航空台東分校', '萬國航空花蓮分校',
      '威莉文理補習班(澎湖)', '立德文理補習班(澎湖)', '佳音英語澎湖分校',
      '佳音英語馬公分校', '佳音英語西嶼分校', '佳音英語望安分校'
    ]
  },
  {
    name: '大陸地區',
    clients: [
      '福州艾倫教育', '長沙協雅教育', '鄭州馮特教育', '內蒙乎和浩特',
      '南京七田教育', '七色花早教中心', '三江星新教育', '中江建明教育',
      '石家莊卓越教育', '廣州維多利亞教育', '深圳西麗天虹美語', '佛山新海豚教育',
      '深圳達人學院', '貴州佳音英語貴陽校', '貴州佳音英語仁懷校',
      '山東濟南佳音英語', '廣州雅思托福英語機構', '廈門艾爾文教育', '中山新世紀教育',
      '武漢高瞻文理補習班', '蘇州英美美語中心', '上海劍橋語言中心', '寧波啟英教育',
      '天津市陽光教育中心', '合肥快樂學習機構', '無錫尚品文教', '哈爾濱快樂英語學校',
      '青島啟智英語', '長春未來之星', '大連樂學堂', '太原佳音美語中心',
      '南寧超越教育', '成都小語種學苑', '重慶摩登英語', '昆明劍橋文理補習班',
      '西安新視野教育', '拉薩博雅教育', '海口美語教育機構', '深圳新思維教育'
    ]
  }

])

// === 根據目前區域，取出 clients 清單 ===
const currentClients = computed(() => {
  return regions.value[currentRegion.value].clients
})

// === 把 clients 切成多頁，每頁 itemsPerSlide 筆 ===
const currentRegionSlides = computed(() => {
  const slides = []
  const total = currentClients.value.length
  for (let i = 0; i < total; i += itemsPerSlide) {
    slides.push(currentClients.value.slice(i, i + itemsPerSlide))
  }
  return slides
})

// === 切換頁面 ===
function prevSlide() {
  if (currentSlide.value > 0) {
    currentSlide.value--
  }
}

function nextSlide() {
  if (currentSlide.value < currentRegionSlides.value.length - 1) {
    currentSlide.value++
  }
}

// === 切換區域時，重設 slide 頁數為 0 ===
watch(currentRegion, () => {
  currentSlide.value = 0
})
</script>

<style lang="scss" scoped>
// 修正 z-index 層級問題
.text-gray-800 {
  position: relative;
  z-index: 1; // 降低 z-index，確保不會蓋過導航欄
}

// Hero section 的背景層級
.absolute.inset-0 {
  z-index: 0;
}

// 裝飾元素層級
.absolute.top-0.right-0 {
  z-index: 0;
}

section.bg-white {
  position: relative;
  z-index: 1;
}

//修正 Swiper 響應式樣式
.swiper-container-fixed {
  position: relative !important;
  width: 100% !important;
  height: 100% !important;
  overflow: hidden !important;
  z-index: 1 !important;
}

.swiper-container-fixed .swiper-wrapper {
  position: relative !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 1 !important;
  display: flex !important;
  transition-property: transform !important;
}

.swiper-container-fixed .swiper-slide {
  flex-shrink: 0 !important;
  width: 100% !important;
  height: 100% !important;
  position: relative !important;
  transition-property: transform !important;
}

.slide-container {
  width: 100% !important;
  height: 100% !important;
  display: block !important;
}

.slide-image {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  display: block !important;
  border-radius: 0.75rem !important;
}

// 修正 Swiper 在極小螢幕的問題
@media (max-width: 480px) {
  .swiper-container-fixed {
    max-height: 192px !important; // h-48 = 192px
    min-height: 192px !important;
  }

  .swiper-container-fixed .swiper-wrapper {
    height: 192px !important;
  }

  .swiper-container-fixed .swiper-slide {
    height: 192px !important;
  }
}

@media (min-width: 481px) and (max-width: 640px) {
  .swiper-container-fixed {
    max-height: 224px !important; // h-56 = 224px
    min-height: 224px !important;
  }
}

// 確保父容器不會影響 Swiper
.bg-white.bg-opacity-10 {
  display: block !important;
  width: 100% !important;
}

// 移除可能干擾的樣式
.swiper-container-fixed * {
  box-sizing: border-box !important;
}

// 修正 Swiper 分頁指示器在小螢幕上的顯示
.swiper {
  --swiper-pagination-color: #facc15;
  --swiper-pagination-bullet-inactive-color: rgba(255, 255, 255, 0.5);
  --swiper-pagination-bullet-size: 8px;
}

@media (max-width: 640px) {
  .swiper {
    --swiper-pagination-bullet-size: 6px;
  }
}

// 確保容器不會被壓縮 - 移除可能造成問題的最小高度
// .bg-white.bg-opacity-10 {
//   min-height: 200px;
// }

// @media (min-width: 640px) {
//   .bg-white.bg-opacity-10 {
//     min-height: 256px;
//   }
// }

// @media (min-width: 1024px) {
//   .bg-white.bg-opacity-10 {
//     min-height: 320px;
//   }
// }

// 強制確保在極小螢幕下的佈局
@media (max-width: 480px) {
  .container {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  // 確保文字區塊顯示
  .text-gray-800 {
    display: block !important;
    width: 100% !important;
    margin-bottom: 1.5rem !important;
  }

  // 確保按鈕可見
  button {
    font-size: 0.875rem !important;
    padding: 0.75rem 1.5rem !important;
  }
}

// Line clamp utility
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

// Smooth animations
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.6s ease-out;
}

// Gradient text
.gradient-text {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
</style>